import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import BlogArticleHeader from '@/components/blog/BlogArticleHeader/BlogArticleHeader';
import BlogRelatedArticles from '@/components/blog/BlogRelatedArticles/BlogRelatedArticles';
import BlogStructuredText from '@/components/blog/BlogStructuredText/BlogStructuredText';
import {
  BLOG_ARTICLE_SELECTOR,
  BLOG_ARTICLES_CARD_SELECTOR,
} from '@/graphql/selectors/blogArticle';
import { Meta } from '@/layouts/Meta';
import type {
  BlogArticleRecordModelType,
  CollectionMetadataModelType,
} from '@/models/api';
import { initializeStore } from '@/models/root-store';
import { Main } from '@/templates/Main';
import type { FaqQuestion } from '@/utils/faqFormatter';
import { convertToFAQSchema } from '@/utils/faqFormatter';

const ARTICLE_FETCH_LIMIT = 100;

type BlogArticleProps = {
  article: BlogArticleRecordModelType;
  relatedArticles: BlogArticleRecordModelType[];
};

const BlogArticle: NextPage<BlogArticleProps> = ({
  article,
  relatedArticles,
}: BlogArticleProps) => {
  // Redirect to blog if the article is not found
  const router = useRouter();
  useEffect(() => {
    if (!article) {
      router.push('/blog');
    }
  }, []);

  if (!article) {
    return null;
  }

  const { content, seoTitle, seoDescription, category, slug, faq } = article;
  const seoFAQSchema = faq ? convertToFAQSchema(faq as FaqQuestion[]) : null;
  return (
    <Main
      meta={
        <Meta
          title={seoTitle || 'Blog Articles'}
          description={seoDescription || 'Blog Articles'}
          canonical={`${process.env.NEXT_PUBLIC_SITE_URL}/blog/${category.slug}/${slug}`}
          seoFAQSchema={seoFAQSchema}
        />
      }
    >
      <div className="p-4 sm:p-8">
        <BlogArticleHeader article={article} />
        <BlogStructuredText content={content} />
        <BlogRelatedArticles articles={relatedArticles} />
      </div>
    </Main>
  );
};

export const getStaticProps: GetStaticProps = async (context: any) => {
  const { slug } = context.params!;
  const store = initializeStore({ ssr: true });

  const { blogArticleCms }: { blogArticleCms: BlogArticleRecordModelType } =
    await store.api.queryBlogArticleCms(
      {
        filter: {
          slug: {
            eq: slug,
          },
        },
        imgixParams: {
          auto: 'format',
        },
      },
      BLOG_ARTICLE_SELECTOR
    );

  if (!blogArticleCms) {
    return {
      props: {
        article: null,
        relatedArticles: null,
      },
    };
  }

  const {
    allBlogArticlesCms,
  }: { allBlogArticlesCms: BlogArticleRecordModelType[] } =
    await store.api.queryAllBlogArticlesCms(
      {
        first: 3,
        orderBy: '_firstPublishedAt_DESC',
        filter: {
          slug: {
            neq: slug,
          },
          category: {
            eq: blogArticleCms.category.id,
          },
        },
        imgixParams: {
          auto: 'format',
        },
      },
      BLOG_ARTICLES_CARD_SELECTOR
    );

  return {
    props: {
      article: blogArticleCms,
      relatedArticles: allBlogArticlesCms,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const store = initializeStore({ ssr: true });

  const {
    _allBlogArticlesMetaCms: { count },
  }: { _allBlogArticlesMetaCms: CollectionMetadataModelType } =
    await store.api.query_allBlogArticlesMetaCms({}, 'count');

  const fetchArticles = async (skip: number) => {
    const {
      allBlogArticlesCms,
    }: { allBlogArticlesCms: BlogArticleRecordModelType[] } =
      await store.api.queryAllBlogArticlesCms(
        { first: ARTICLE_FETCH_LIMIT, skip },
        BLOG_ARTICLES_CARD_SELECTOR
      );
    return allBlogArticlesCms;
  };

  const articleCount = count as number;
  const allArticlePromises = [];
  for (let i = 0; i < articleCount; i += ARTICLE_FETCH_LIMIT) {
    allArticlePromises.push(fetchArticles(i));
  }
  const result = await Promise.all(allArticlePromises);
  const allArticles = result.flat();
  const paths = allArticles.map((article) => {
    return {
      params: {
        slug: article.slug as string,
        category: article.category.slug as string,
      },
    };
  });

  return {
    paths,
    fallback: true,
  };
};

export default BlogArticle;
